import React, { type ReactElement, type ReactNode } from 'react';

import classnames from 'classnames';

import { Icon, type IconColor, type IconName } from '../Icon';
import { Text } from '../Typography';

import styles from './Alert.module.scss';

export type AlertType = 'error' | 'information' | 'success' | 'warning';

type AlertProps = {
    className?: string;
    message: ReactNode;
    type?: AlertType;
    hideIcon?: boolean;
    fullWidth?: boolean;
    children?: ReactNode;
    action?: ReactNode;
};

const ICON_NAMES: Record<AlertType, IconName> = {
    error: 's-icon-circle-exclamation',
    success: 's-icon-circle-check',
    warning: 's-icon-circle-exclamation',
    information: 's-icon-circle-info'
};

const ICON_COLORS: Record<AlertType, IconColor> = {
    error: 'danger',
    success: 'success',
    warning: 'warning',
    information: 'information'
};

export const Alert = ({
    className,
    message,
    type = 'information',
    hideIcon,
    fullWidth,
    children,
    action
}: AlertProps): ReactElement => {
    const alertStyles = classnames(styles.alertContainer, styles[type], className, {
        [styles.fullWidth]: fullWidth
    });

    const iconName = ICON_NAMES[type];
    const iconColor = ICON_COLORS[type];

    return (
        <div role="alert" className={alertStyles}>
            <div className={styles.alert}>
                {!hideIcon && (
                    <Icon
                        name={iconName}
                        color={iconColor}
                        customSize={20}
                        className={styles.alertIcon}
                        aria-label={type}
                    />
                )}
                <div className={styles.message}>
                    <Text>{message}</Text>
                </div>
                {Boolean(action) && <div className={styles.action}>{action}</div>}
            </div>
            {children}
        </div>
    );
};

Alert.displayName = 'Alert';
